import { useWaveStore } from '@/stores/WaveStore';
import Wave from 'react-wavify'
import { useMemo, useEffect, useState, useRef } from "react";
import { motion } from 'framer-motion';
import { useTheme } from 'next-themes';

export default function TimerBackground(props: { children?: React.ReactNode }) {
  const { waveValue, colorIndex } = useWaveStore();
  // theme 仅用于主文档环境下的依赖，但我们会添加 MutationObserver 以适配 PiP
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [resolvedColors, setResolvedColors] = useState({
    bottom: '#60a5fa',
    l1: '#3b82f6',
    l2: '#2563eb',
    l3: '#1d4ed8',
    top: '#3270eb'
  });
  const [themeVersion, setThemeVersion] = useState(0);

  const waveHeight = useMemo(() => {
    return 100 - Math.min(Math.max(waveValue, 0), 100);
  }, [waveValue]);

  // 监听渲染容器所属文档的主题属性变化（class / data-theme），在 PiP 与主文档均可工作
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const doc = containerRef.current?.ownerDocument || document;
    const target = doc.documentElement;
    const observer = new MutationObserver(() => setThemeVersion(v => v + 1));
    observer.observe(target, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => observer.disconnect();
  }, []);

  // 根据当前文档（主文档或 PiP 文档）解析 CSS 变量并更新波浪颜色
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const doc = containerRef.current?.ownerDocument || document;
    const win = doc.defaultView || window;
    // 使用 setTimeout 确保 CSS 变量在主题切换后已更新
    const timer = setTimeout(() => {
      const currentColorTheme = useWaveStore.getState().getColorTheme();
      const root = doc.documentElement;
      const computedStyle = win.getComputedStyle(root);
      const pick = (cssVar: string) => computedStyle.getPropertyValue(cssVar.replace('var(', '').replace(')', '')).trim() || cssVar;
      const newColors = {
        bottom: pick(currentColorTheme.bottom),
        l1: pick(currentColorTheme.l1),
        l2: pick(currentColorTheme.l2),
        l3: pick(currentColorTheme.l3),
        top: pick(currentColorTheme.top),
      };
      setResolvedColors(newColors);
    }, 10);

    return () => clearTimeout(timer);
  }, [colorIndex, theme, themeVersion]);

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      {/* 底部背景色 - 始终覆盖整个屏幕 */}
      <div className={'z-1 w-full absolute h-full'} style={{
        backgroundColor: resolvedColors.bottom,
      }} />

      {/* Wave容器 - 固定高度，避免抖动 */}
      <motion.div
        className="absolute w-full pointer-events-none z-3"
        animate={{
          top: `${waveHeight}%`
        }}
        transition={{
          type: "tween",
          ease: "linear",
          duration: 0.1
        }}
        style={{
          height: '100%',
        }}
      >
        <Wave fill={resolvedColors.l1}
          paused={false}
          className='absolute w-full h-full pointer-events-none z-3'
          options={{
            height: 20,
            amplitude: 20,
            speed: 0.2,
            points: 4
          }}
        />
        <Wave fill={resolvedColors.l2}
          paused={false}
          className='absolute w-full h-full pointer-events-none z-3'
          options={{
            height: 20,
            amplitude: 21,
            speed: 0.18,
            points: 5
          }}
        />
        <Wave fill={resolvedColors.l3}
          paused={false}
          className='absolute w-full h-full pointer-events-none z-3'
          options={{
            height: 20,
            amplitude: 22,
            speed: 0.16,
            points: 6
          }}
        />
      </motion.div>

      {/* 内容区域 */}
      <div className="relative z-10 w-full h-full flex">
        {props.children}
      </div>
    </div>
  );
}

